import { IconExclamationCircle } from "@tabler/icons-react";
import clsx from "clsx";
import { useContext, useEffect, useRef, useState } from "react";
import zenScroll from "zenscroll";
import { pause } from "../Utils";
import { ModalContext } from "../contexts/Contexts";
import { Button, swapLabel } from "./Button";
import { getScrollElement } from "./Modal";

/**
 * @param {{
 *   actionLabel: ((confirmAction: boolean) => string) | string;
 *   actionIcon: TablerIcon;
 *   cancelLabel: string;
 *   warningHeader: JSX.Element;
 *   warningMessage: JSX.Element;
 *   willClose: boolean;
 *   confirmState: UseState<boolean>;
 *   onActionConfirm: function;
 * }} props
 */
const DestructiveActionButton = (props) => {
  const {
    actionLabel,
    actionIcon: ActionIcon,
    cancelLabel,
    warningHeader,
    warningMessage,
    willClose,
    confirmState,
    onActionConfirm,
  } = props;

  const [confirmAction, setConfirmAction] = confirmState;
  const [showCancelBtn, setShowCancelBtn] = useState(false);
  const actionWarnRef = useRef(null);
  const actionBtnRef = useRef(null);

  const { contentElement } = useContext(ModalContext);

  useEffect(() => {
    if (willClose && confirmAction) cancelAction();
  }, [willClose]);

  async function showActionConfirm() {
    /** @type {Element} */
    const actionBtn = actionBtnRef.current;
    /** @type {Element} */
    const actionWarn = actionWarnRef.current;
    if (!actionBtn || !actionWarn) return;

    const fullHeight = actionWarn.scrollHeight;
    actionWarn.style.maxHeight = "0px";
    swapLabel(actionBtn, () => {
      actionWarn.style.maxHeight = `${fullHeight}px`;
      setShowCancelBtn(true);
      setConfirmAction(true);
    });
    await pause(100);
    actionWarn.style.maxHeight = null;
    if (actionBtn.matches(":focus-visible")) {
      actionBtn.nextElementSibling?.focus();
    }

    await pause(200);
    const scrollElt = getScrollElement(contentElement);
    if (scrollElt) {
      const { scrollHeight, clientHeight, scrollTop } = scrollElt;
      const scrollDistance = scrollHeight + clientHeight - scrollTop;
      zenScroll
        .createScroller(scrollElt)
        .toY(scrollElt.scrollHeight, scrollDistance);
    }
  }

  async function cancelAction() {
    /** @type {Element} */
    const actionBtn = actionBtnRef.current;
    /** @type {Element} */
    const actionWarn = actionWarnRef.current;
    if (!actionBtn || !actionWarn) return;

    setShowCancelBtn(false);
    actionWarn.style.maxHeight = `${actionWarn.clientHeight}px`;
    swapLabel(actionBtn, () => {
      setConfirmAction(false);
      actionWarn.style.maxHeight = "0px";
    });
    await pause(100);
    actionWarn.style.maxHeight = null;
    if (actionBtn.nextElementSibling?.matches(":focus-visible")) {
      actionBtn.focus();
    }
  }

  return (
    <>
      <header
        className={clsx("action-warn", !confirmAction && "hidden")}
        ref={actionWarnRef}
      >
        <IconExclamationCircle height="3em" width="3em" stroke={1.3} />
        <div className="msg">
          <b>{warningHeader}</b>
          {warningMessage}
        </div>
      </header>

      <div className="btn-row">
        <Button
          icon={
            ActionIcon?.prototype &&
            !confirmAction && (
              <ActionIcon
                style={{
                  boxSizing: "border-box",
                  paddingRight: "0.4rem",
                }}
                stroke={2.4}
              />
            )
          }
          label={actionLabel?.call ? actionLabel(confirmAction) : actionLabel}
          styles={{ width: "100%" }}
          bgColor="var(--danger-color)"
          onClick={confirmAction ? onActionConfirm : showActionConfirm}
          ref={actionBtnRef}
        />
        <Button
          label={cancelLabel}
          variant="secondary"
          onClick={cancelAction}
          styles={{
            marginLeft: showCancelBtn ? "0px" : "-1rem",
            opacity: showCancelBtn ? 1 : 0,
            flex: showCancelBtn ? 1 : 0,
            maxWidth: "fit-content",
          }}
          tabIndex={!showCancelBtn && -1}
        />
      </div>
    </>
  );
};

export default DestructiveActionButton;
